import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { MenuItemAction } from 'menu/MenuItem';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectActiveScene, selectFrame } from 'redux/db/reducers';
import { Direction, getAdjacentFrame } from 'redux/db/util';
import { openCanvasLeft, openCanvasWidth } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import * as routes from 'routes';
import DsControls from './DsControls';
import * as S from './styles';

interface FrameBarProps {
	mode: 'focus' | 'prototype';
	frameId?: string;
	onClose?: () => void;
}

const FrameBar = ({ mode, frameId: propFrameId, onClose }: FrameBarProps) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const [tooltipId] = useState(String(Math.random()));

	const { frame, scene, canvasLeft, canvasWidth, centeredFrameId } = useSelector(
		(r: RootReducer) => ({
			frame: propFrameId
				? selectFrame(r.db, propFrameId)
				: r.mode.centeredFrameId
				? selectFrame(r.db, r.mode.centeredFrameId)
				: null,
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			canvasLeft: openCanvasLeft(r.mode),
			canvasWidth: openCanvasWidth(r.mode),
			centeredFrameId: r.mode.centeredFrameId,
		}),
		shallowEqual
	);

	const frameId = propFrameId ?? centeredFrameId;
	const iconSize = 18;
	const getArrowAction = (direction: Direction): { disabled: boolean; callback: () => void } => {
		if (!frameId) return { disabled: true, callback: () => {} };

		if (mode === 'focus') {
			const action = MenuItemAction.createNavFromFrame(frameId, direction);
			return { disabled: !!action.disabled, callback: () => action.callback() };
		}
		// Prototype mode
		const adjacent = getAdjacentFrame(frameId, direction);
		return {
			disabled: !(adjacent && scene),
			callback: () => {
				if (adjacent && scene) {
					navigate(routes.frame(scene.id, scene.title, adjacent.id));
				}
			},
		};
	};

	const leftAction = getArrowAction(Direction.LEFT);
	const rightAction = getArrowAction(Direction.RIGHT);
	const upAction = getArrowAction(Direction.UP);
	const downAction = getArrowAction(Direction.DOWN);

	// Keep the keydown handler reading the latest actions without re-registering
	// the listener on every render. The action objects are recreated each render
	// (new closures over scene/navigate), so depending on them directly would
	// tear down and rebind the window listener on every parent update.
	const actionsRef = useRef({ leftAction, rightAction, upAction, downAction });
	actionsRef.current = { leftAction, rightAction, upAction, downAction };

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	const onPlayPress = () => {
		if (frameId) MenuItemAction.createOpenPrototype(frameId).callback();
	};

	const onActionPress = () => {
		if (mode === 'prototype') {
			onClose?.();
		} else {
			onPlayPress();
		}
	};

	//----------------------------------------------------------------------
	// KEYBOARD
	//----------------------------------------------------------------------
	// Prototype mode is a separate route without the canvas-global keyboard handler, so the
	// bar owns arrow navigation there. In focus mode the canvas-global handler in
	// redux/mode/util/keyboard.ts already handles arrow nav for the selected frame — registering
	// a second listener here would fire both and skip a frame on each press.
	useEffect(() => {
		if (!frameId || mode !== 'prototype') return;

		const onKeyDown = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement;
			const consumesArrowKeys =
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.isContentEditable ||
				target.closest('input, textarea, [contenteditable="true"]');

			if (consumesArrowKeys) return;

			const current = actionsRef.current;
			const actions: Record<string, { disabled: boolean; callback: () => void }> = {
				ArrowLeft: current.leftAction,
				ArrowRight: current.rightAction,
				ArrowUp: current.upAction,
				ArrowDown: current.downAction,
			};

			const action = actions[e.key];
			if (action && !action.disabled) {
				e.preventDefault();
				action.callback();
			}
		};

		window.addEventListener('keydown', onKeyDown, true);
		return () => window.removeEventListener('keydown', onKeyDown, true);
	}, [frameId, mode]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	if (!frame || !frameId) return null;

	return (
		<>
			<S.Bar mode={mode} left={canvasLeft} width={canvasWidth}>
				<S.BarContent>
					<S.LeftGroup>
						<S.ArrowButton
							aria-label="Move left"
							disabled={!!leftAction.disabled}
							onClick={() => !leftAction.disabled && leftAction.callback()}
						>
							<Icon
								type={IconType.LINE_ARROW_LEFT}
								width={iconSize}
								height={iconSize}
								color={leftAction.disabled ? theme.gray[400] : theme.gray[1000]}
							/>
						</S.ArrowButton>
						<S.ArrowSpacer />
						<S.ArrowButton
							aria-label="Move right"
							disabled={!!rightAction.disabled}
							onClick={() => !rightAction.disabled && rightAction.callback()}
						>
							<Icon
								type={IconType.LINE_ARROW_RIGHT}
								width={iconSize}
								height={iconSize}
								color={rightAction.disabled ? theme.gray[400] : theme.gray[1000]}
							/>
						</S.ArrowButton>
						<S.ArrowSpacer />
						<S.ArrowButton
							aria-label="Move up"
							disabled={!!upAction.disabled}
							onClick={() => !upAction.disabled && upAction.callback()}
						>
							<Icon
								type={IconType.LINE_ARROW_UP}
								width={iconSize}
								height={iconSize}
								color={upAction.disabled ? theme.gray[400] : theme.gray[1000]}
							/>
						</S.ArrowButton>
						<S.ArrowSpacer />
						<S.ArrowButton
							aria-label="Move down"
							disabled={!!downAction.disabled}
							onClick={() => !downAction.disabled && downAction.callback()}
						>
							<Icon
								type={IconType.LINE_ARROW_DOWN}
								width={iconSize}
								height={iconSize}
								color={downAction.disabled ? theme.gray[400] : theme.gray[1000]}
							/>
						</S.ArrowButton>
					</S.LeftGroup>

					<S.HitArea
						onClick={onActionPress}
						data-tooltip-id={mode === 'prototype' ? undefined : tooltipId}
						data-tooltip-content={mode === 'prototype' ? undefined : 'View prototype'}
					>
						{mode === 'prototype' && <S.GhostLabel>click anywhere to exit</S.GhostLabel>}
					</S.HitArea>

					{mode === 'focus' && (
						<S.CenterGroup>
							<S.ActionButton active={true}>
								<Icon
									type={IconType.PLAY}
									width={iconSize}
									height={iconSize}
									color={theme.gray[1000]}
								/>
							</S.ActionButton>
						</S.CenterGroup>
					)}

					<S.RightGroup>
						<DsControls frame={frame} />
						<S.Dimensions>
							{Math.round(frame.width)} × {Math.round(frame.height)}
						</S.Dimensions>
					</S.RightGroup>
				</S.BarContent>
				<Tooltip id={tooltipId} placement="top" />
			</S.Bar>
		</>
	);
};

export default FrameBar;
